<template>
  <div class="slide slide-highlights-grid">
    <div class="columns-container">
      <!-- Strengths Column -->
      <div class="highlight-column strengths">
        <div class="column-header">
          <div class="header-stripe"></div>
          <h3 class="column-title">{{ $t('reports.quarterly.highlights.strengths_title') }}</h3>
          <p class="column-subtitle">{{ $t('reports.quarterly.highlights.strengths_subtitle') }}</p>
        </div>
        <div class="cards-list">
          <div
            v-for="(item, index) in data.strengths"
            :key="`strength-${index}`"
            class="highlight-card"
            :style="{ animationDelay: `${index * 0.08}s` }"
          >
            <div class="card-score score-high">{{ item.score }}%</div>
            <div class="card-label">{{ item.label }}</div>
          </div>
          <div v-if="!data.strengths || !data.strengths.length" class="empty-state">
            <span>{{ $t('reports.quarterly.highlights.empty_strengths') }}</span>
          </div>
        </div>
      </div>

      <!-- Challenges Column -->
      <div class="highlight-column challenges">
        <div class="column-header">
          <div class="header-stripe"></div>
          <h3 class="column-title">{{ $t('reports.quarterly.highlights.challenges_title') }}</h3>
          <p class="column-subtitle">{{ $t('reports.quarterly.highlights.challenges_subtitle') }}</p>
        </div>
        <div class="cards-list">
          <div
            v-for="(item, index) in data.challenges"
            :key="`challenge-${index}`"
            class="highlight-card"
            :style="{ animationDelay: `${index * 0.08 + 0.2}s` }"
          >
            <div class="card-score score-low">{{ item.score }}%</div>
            <div class="card-label">{{ item.label }}</div>
          </div>
          <div v-if="!data.challenges || !data.challenges.length" class="empty-state">
            <span>{{ $t('reports.quarterly.highlights.empty_challenges') }}</span>
          </div>
        </div>
      </div>

      <!-- Priorities Column -->
      <div class="highlight-column priorities">
        <div class="column-header">
          <div class="header-stripe"></div>
          <h3 class="column-title">{{ $t('reports.quarterly.highlights.priorities_title') }}</h3>
          <p class="column-subtitle">{{ $t('reports.quarterly.highlights.priorities_subtitle') }}</p>
        </div>
        <div class="cards-list">
          <div
            v-for="(item, index) in data.priorities"
            :key="`priority-${index}`"
            class="highlight-card priority-card"
            :style="{ animationDelay: `${index * 0.08 + 0.4}s` }"
          >
            <div class="priority-number">{{ index + 1 }}</div>
            <div class="card-label">{{ item.label }}</div>
          </div>
          <div v-if="!data.priorities || !data.priorities.length" class="empty-state">
            <span>{{ $t('reports.quarterly.highlights.empty_priorities') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HighlightsGridSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-highlights-grid {
  @extend .slide-base;
  background: $paper;
  padding: 80px;
}

.columns-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  height: 100%;
}

.highlight-column {
  display: flex;
  flex-direction: column;
}

.column-header {
  margin-bottom: 32px;
  position: relative;
}

.header-stripe {
  height: 4px;
  width: 60px;
  border-radius: 2px;
  margin-bottom: 20px;

  .strengths & { background: linear-gradient(90deg, $emerald, darken($emerald, 10%)); }
  .challenges & { background: linear-gradient(90deg, $amber, darken($amber, 10%)); }
  .priorities & { background: linear-gradient(90deg, $violet-500, $violet-700); }
}

.column-title {
  font-family: $font-body;
  font-size: 28px;
  font-weight: 700;
  color: $ink;
  margin: 0 0 8px;
}

.column-subtitle {
  font-family: $font-body;
  font-size: 14px;
  color: $ink-subtle;
  margin: 0;
}

.cards-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
}

.highlight-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: $shadow-soft;
  display: flex;
  align-items: center;
  gap: 20px;
  animation: scaleIn 0.4s cubic-bezier(0.25, 1, 0.5, 1) backwards;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: $shadow-medium;
  }
}

.card-score {
  font-family: $font-body;
  font-size: 24px;
  font-weight: 700;
  min-width: 70px;

  &.score-high { color: $emerald; }
  &.score-medium { color: $amber; }
  &.score-low { color: $rose; }
}

.card-label {
  font-family: $font-body;
  font-size: 16px;
  color: $ink;
  line-height: 1.4;
  flex: 1;
}

.priority-card {
  .priority-number {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: $violet-500;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: $font-body;
    font-size: 18px;
    font-weight: 700;
    flex-shrink: 0;
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: $ink-subtle;
  font-family: $font-body;
  font-size: 14px;
  background: rgba($ink, 0.02);
  border-radius: 16px;
  padding: 40px;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
